/**
 * GdziePublikowac — "Gdzie są Twoi klienci"
 * AI-powered recommendations for where to publish posts
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import PanelLayout from '@/components/PanelLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import {
  MapPin, RefreshCw, Flame, TrendingUp, Minus,
  ExternalLink, Users, Globe, ShoppingBag,
  Sparkles, Target, ArrowRight, Zap
} from 'lucide-react';

const POTENTIAL_CONFIG = {
  wysoki: { label: 'Wysoki', icon: Flame, color: '#F47A3B', bg: 'rgba(244,122,59,0.08)', border: 'rgba(244,122,59,0.20)' },
  sredni: { label: 'Sredni', icon: TrendingUp, color: '#EAB308', bg: 'rgba(234,179,8,0.08)', border: 'rgba(234,179,8,0.20)' },
  niski:  { label: 'Niski',  icon: Minus, color: '#94A3B8', bg: 'rgba(148,163,184,0.08)', border: 'rgba(148,163,184,0.20)' },
};

const TYPE_ICONS = {
  grupa_fb: Users,
  strona_fb: Globe,
  marketplace: ShoppingBag,
  lokalna_grupa: MapPin,
  tematyczna_grupa: Target,
};

function PotentialBadge({ potential }) {
  const cfg = POTENTIAL_CONFIG[potential] || POTENTIAL_CONFIG.sredni;
  const Icon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold"
      style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}
      data-testid={`potential-badge-${potential}`}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function RecommendationCard({ rec, index }) {
  const TypeIcon = TYPE_ICONS[rec.type] || Globe;
  const potCfg = POTENTIAL_CONFIG[rec.potential] || POTENTIAL_CONFIG.sredni;
  const isHigh = rec.potential === 'wysoki';

  return (
    <motion.a
      href={`https://www.facebook.com/search/groups?q=${encodeURIComponent(rec.name)}`}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`block rounded-2xl p-6 transition-all hover:scale-[1.01] ${
        isHigh
          ? 'bg-gradient-to-br from-[#FFF7ED] to-white border-2 border-[#F47A3B]/20 hover:border-[#F47A3B]/40'
          : 'bg-white border border-zinc-100 hover:border-zinc-200'
      }`}
      data-testid={`recommendation-card-${index}`}
    >
      {/* TOP: badge + potential */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: potCfg.bg }}
          >
            <TypeIcon className="w-4 h-4" style={{ color: potCfg.color }} />
          </div>
          <PotentialBadge potential={rec.potential} />
        </div>
        <ExternalLink className="w-3.5 h-3.5 text-zinc-300" />
      </div>

      {/* MIDDLE: name — biggest element */}
      <h3 className={`font-black leading-tight mb-2 ${isHigh ? 'text-[18px] text-zinc-900' : 'text-[16px] text-zinc-800'}`}>
        {rec.name}
      </h3>

      {/* BOTTOM: 1 short why */}
      {rec.why && (
        <p className="text-[12px] text-zinc-400 leading-snug line-clamp-1">{rec.why.split('.')[0]}</p>
      )}
    </motion.a>
  );
}

function CategoryFilter({ categories, active, onChange }) {
  return (
    <div className="flex gap-2 flex-wrap" data-testid="category-filter">
      <button
        onClick={() => onChange(null)}
        className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-colors ${
          !active ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
        }`}
        data-testid="filter-all"
      >
        Wszystkie
      </button>
      {categories.map(cat => (
        <button
          key={cat}
          onClick={() => onChange(cat)}
          className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-colors capitalize ${
            active === cat ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
          }`}
          data-testid={`filter-${cat}`}
        >
          {cat}
        </button>
      ))}
    </div>
  );
}

export default function GdziePublikowac() {
  const { t } = useTranslation();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchRecommendations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/recommendations/publish-locations`, { headers });
      setRecommendations(res.data.recommendations || []);
      setGeneratedAt(res.data.generated_at);
    } catch (e) {
      if (e.response?.status === 404) {
        toast.error('Uzupełnij profil, żeby uzyskać rekomendacje');
      } else {
        toast.error('Nie udało się pobrać rekomendacji');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/recommendations/refresh-locations`, {}, { headers });
      setRecommendations(res.data.recommendations || []);
      setGeneratedAt(res.data.generated_at);
      toast.success('Rekomendacje odświeżone');
    } catch (e) {
      toast.error('Nie udało się odświeżyć');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchRecommendations(); }, []); // eslint-disable-line

  const categories = [...new Set(recommendations.map(r => r.category).filter(Boolean))];
  const filtered = activeCategory
    ? recommendations.filter(r => r.category === activeCategory)
    : recommendations;

  const highCount = recommendations.filter(r => r.potential === 'wysoki').length;

  return (
    <PanelLayout>
      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="gdzie-publikowac-page">
        {/* Header — standard pattern */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F47A3B]/10 border border-[#F47A3B]/15 mb-4">
            <MapPin className="w-3.5 h-3.5 text-[#F47A3B]" />
            <span className="text-[12px] font-semibold text-[#F47A3B]">Twoi klienci, Twój zasięg</span>
          </div>
          <h1 className="text-3xl font-extrabold text-zinc-900 mb-2" data-testid="page-title">
            Gdzie publikować
          </h1>
          <p className="text-[15px] text-zinc-500 max-w-md mx-auto">
            Analizujemy Twój profil i podpowiadamy najlepsze miejsca, żeby zdobywać klientów.
          </p>
        </div>

        {/* Stats bar */}
        {!loading && recommendations.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-gradient-to-r from-zinc-900 to-zinc-800 rounded-2xl p-4 flex items-center justify-between"
            data-testid="stats-bar"
          >
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#F47A3B]" />
                <span className="text-white text-[13px] font-bold">{recommendations.length} miejsc</span>
              </div>
              <div className="flex items-center gap-2">
                <Flame className="w-4 h-4 text-[#F47A3B]" />
                <span className="text-zinc-300 text-[12px]">{highCount} z wysokim potencjałem</span>
              </div>
              {generatedAt && (
                <span className="text-zinc-500 text-[11px]">
                  Ostatnia analiza: {new Date(generatedAt).toLocaleDateString('pl-PL')}
                </span>
              )}
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/10 text-white text-[11px] font-bold hover:bg-white/20 transition-colors disabled:opacity-50"
              data-testid="refresh-recommendations-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Analizuję...' : 'Odśwież'}
            </button>
          </motion.div>
        )}

        {/* Category filter */}
        {!loading && categories.length > 1 && (
          <div className="mb-5">
            <CategoryFilter categories={categories} active={activeCategory} onChange={setActiveCategory} />
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20" data-testid="loading-state">
            <div className="w-12 h-12 rounded-2xl bg-[#F47A3B]/10 flex items-center justify-center mb-4">
              <Sparkles className="w-6 h-6 text-[#F47A3B] animate-pulse" />
            </div>
            <p className="text-[14px] font-bold text-zinc-700">Analizuję Twój profil...</p>
            <p className="text-[12px] text-zinc-400 mt-1">Szukam najlepszych miejsc dla Twojego biznesu</p>
          </div>
        )}

        {/* Recommendations grid */}
        {!loading && filtered.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="recommendations-list">
            {filtered.map((rec, i) => (
              <RecommendationCard key={i} rec={rec} index={i} />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && recommendations.length === 0 && (
          <div className="max-w-lg mx-auto text-center py-8" data-testid="empty-state">
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
              <div className="w-16 h-16 rounded-2xl bg-[#F47A3B]/10 flex items-center justify-center mx-auto mb-5">
                <MapPin className="w-8 h-8 text-[#F47A3B]" />
              </div>
              <h2 className="text-2xl font-extrabold text-zinc-900 mb-2">{t('gdziePublikowac.title')}</h2>
              <p className="text-[15px] text-zinc-500 mb-8 max-w-md mx-auto">
                Znajdziemy najlepsze grupy i miejsca w sieci, gdzie są Twoi potencjalni klienci.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl border border-zinc-200 p-6 mb-6 text-left">
              <h3 className="text-sm font-bold text-zinc-900 mb-4">Jak to działa?</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-lg bg-[#F47A3B]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Target className="w-4 h-4 text-[#F47A3B]" />
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-zinc-800">Analizujemy Twój profil</p>
                    <p className="text-[12px] text-zinc-500">Na podstawie branży, lokalizacji i oferty</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Globe className="w-4 h-4 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-zinc-800">Szukamy grup i forów</p>
                    <p className="text-[12px] text-zinc-500">Grupy FB, fora, portale branżowe — tam gdzie są klienci</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Users className="w-4 h-4 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-zinc-800">Podajemy gotowe rekomendacje</p>
                    <p className="text-[12px] text-zinc-500">Z oceną potencjału — wiesz gdzie warto, a gdzie nie</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <p className="text-[12px] text-zinc-400 mb-3">Uzupełnij profil, żeby system mógł znaleźć najlepsze miejsca</p>
              <button onClick={() => window.location.href = '/panel/profil'}
                className="inline-flex items-center gap-2 bg-[#F47A3B] hover:bg-[#E06A2E] text-white rounded-xl px-8 py-3 text-[14px] font-bold shadow-lg shadow-[#F47A3B]/20 transition-all"
                data-testid="empty-cta-profile">
                Uzupełnij profil <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          </div>
        )}

        {/* Bottom CTA */}
        {!loading && recommendations.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-8 bg-zinc-50 rounded-2xl p-5 flex items-center justify-center gap-6 text-[11px] text-zinc-400"
            data-testid="bottom-cta"
          >
            <span className="flex items-center gap-1"><ArrowRight className="w-3 h-3" /> Dolacz do grupy</span>
            <span className="flex items-center gap-1"><ArrowRight className="w-3 h-3" /> Publikuj post</span>
            <span className="flex items-center gap-1"><ArrowRight className="w-3 h-3" /> Zdobadz klienta</span>
          </motion.div>
        )}
      </div>
    </PanelLayout>
  );
}
